import { Trans, useTranslation } from 'react-i18next';
import { GovIcon } from '@gov-design-system-ce/react';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	DATABOX_FOLDER_LABELS,
	dataBoxLabel,
	getDataBoxTypeLabel,
	useDataBoxMessageListContext,
	useDataBoxSearchFormContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { Button, EmptyMessage } from '@gov-nx/ui/web';
import { MarkdownRender } from '../../Markdown/MarkdownRender';
import { DataBoxMessageList } from './DataBoxMessageList';
import { DataBoxMessageListMainSkeleton } from './DataBoxMessageListMainSkeleton';

export const DataBoxMessageListMain = () => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const { t } = useTranslation([PageCode['datove-schranky']]);

	const {
		controls,
		messageList,
		isLoadMorePossible,
		loadMore,
		messagesFilter: { query, hasSearch, folderType },
		previousSuggestions,
		deleteSuggestion,
		nextDataBoxForSearch,
		isQueryLoading,
	} = useDataBoxMessageListContext();

	const isLoading = controls.processLoading || isQueryLoading;

	const { showRelevantSuggestions, setSearchInputValue } =
		useDataBoxSearchFormContext();

	const content = () => {
		if (hasSearch) {
			if (typeof query === 'string' && query.length < 3) {
				return (
					<p className="px-4 py-4 text-body-s text-(--text-secondary)">
						{tn('vyhledavani.vyhledavany-vyraz')}
					</p>
				);
			} else if (!query) {
				if (previousSuggestions.length > 0) {
					return (
						<div>
							<p className="px-(--spacing-m) py-(--spacing-s) text-body-s-bold! text-(--text-secondary) bg-(--background-primary-subtlest)">
								{tn('vyhledavani.nedavno-vyhledavane')}
							</p>
							{showRelevantSuggestions(previousSuggestions).map(
								(suggestion) => (
									<div
										key={suggestion}
										className="flex gap-4 items-center justify-between px-4">
										<GovIcon
											name="clock-history"
											type="templates"
											className="w-4 h-4 grow-0 shrink-0"
										/>
										<div
											onClick={() => setSearchInputValue(suggestion)}
											className="w-[274px] mr-auto overflow-hidden whitespace-nowrap text-ellipsis cursor-pointer">
											{suggestion}
										</div>
										<Button
											color="neutral"
											type="base"
											size="m"
											onClick={() => deleteSuggestion(suggestion)}>
											<GovIcon
												name="x"
												type="components"
												slot="icon-end"
											/>
										</Button>
									</div>
								)
							)}
						</div>
					);
				} else {
					return (
						<>
							<div className="flex flex-col gap-2 px-4 py-6 [ ds:hidden! ]">
								<p className="flex gap-2 items-center font-bold">
									<GovIcon
										name="lightbulb-fill"
										type="components"
										size="m"
									/>
									{tn('info-stranka.vyhledavani.podle')}
								</p>
								<ul className="text-(--text-secondary)">
									<li>{tn('info-stranka.vyhledavani.predmet')}</li>
									<li>{tn('info-stranka.vyhledavani.odesilatel')}</li>
									<li>{tn('info-stranka.vyhledavani.schranka')}</li>
									<li>{tn('info-stranka.vyhledavani.zprava')}</li>
								</ul>
								<p>
									<Trans
										t={t}
										i18nKey="info-stranka.vyhledavani-slozka"
										components={{ strong: <strong /> }}
									/>
								</p>
							</div>
							<p className="hidden! px-4 py-4 text-body-s text-(--text-secondary) [ ds:block! ]">
								{tn('vyhledavani.vychozi-hlaska')}
							</p>
						</>
					);
				}
			} else if (!messageList?.length && !isLoading) {
				return (
					<EmptyMessage
						headline={tn('vyhledavani.vyhledani-nebylo-uspesne', {
							vyraz: query,
						})}
						image="empty"
						align="left">
						<p>
							<strong>{tn('vyhledavani.tipy')}</strong>
						</p>
						<p>
							{tn('info-stranka.vyhledavani.podle')}
							<ul>
								<li>
									<strong>{tn('info-stranka.vyhledavani.predmet')}</strong>
								</li>
								<li>
									<strong>{tn('info-stranka.vyhledavani.odesilatel')}</strong>
								</li>
								<li>
									<strong>{tn('info-stranka.vyhledavani.schranka')}</strong>
								</li>
								<li>
									<strong>{tn('info-stranka.vyhledavani.zprava')}</strong>
								</li>
							</ul>
						</p>
						<MarkdownRender>{tn('vyhledavani.ujistete-se')}</MarkdownRender>
						{nextDataBoxForSearch && (
							<MarkdownRender>
								{t('vyhledavani.zkuste-vyhledat-jinde', {
									vyraz: query,
									slozka: tn(DATABOX_FOLDER_LABELS[folderType]),
									schrankaId: nextDataBoxForSearch.datovaSchrankaId,
									'nazev-schranky': dataBoxLabel(nextDataBoxForSearch, true),
									'typ-schranky': getDataBoxTypeLabel(
										nextDataBoxForSearch.typSchranky,
										tn
									),
								})}
							</MarkdownRender>
						)}
						<p>{tn('vyhledavani.zkontrolujte-preklepy')}</p>
					</EmptyMessage>
				);
			}
		}
		return (
			<>
				{isLoading ? (
					<DataBoxMessageListMainSkeleton />
				) : (
					<DataBoxMessageList messageList={messageList} />
				)}

				{isLoadMorePossible && !isLoading && (
					<div className="py-4 border-t border-(--border-subtlest) text-center">
						<Button
							color="primary"
							type="outlined"
							size="s"
							onClick={loadMore}
							aria-label={tn('zpravy.vypis.nacist-dalsi')}>
							{tn('zpravy.vypis.nacist-dalsi')}
						</Button>
					</div>
				)}
			</>
		);
	};

	return <section className="flex-1 overflow-y-auto">{content()}</section>;
};
